import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent, Optional} from 'util/Types';

export interface ExtraActionButtons {
  viewMode: Array<JSX.Element>;
  editMode: Array<JSX.Element>;
}

interface BaseProps {
  archive?: Optional<() => void>;
  cancel?: Optional<() => void>;
  complete?: Optional<() => void | Promise<void>>;
  copyAPIID?: Optional<() => void | Promise<void>>;
  edit?: Optional<(e: ElementMouseEvent) => void | Promise<void>>;
  remove?: Optional<(e?: ElementMouseEvent) => void>;
  unarchive?: Optional<() => void>;
  className: string;
  isCompact?: boolean;
  isCompleteEnabled?: boolean;
  isEditing: boolean;
  extraActionButtons?: ExtraActionButtons;
  disableArchiveButtonWithTooltip?: string | null;
  disableDeleteButtonWithTooltip?: string | null;
}

export interface FieldActionButtonsProps extends BaseProps {
  isEditing: boolean;
  cancel?: () => void;
  complete?: () => void | Promise<void>;
  isCompleteEnabled?: boolean;
  isCancelEnabled?: boolean;
  hasOuterMargin?: boolean;
  isArchiveEnabled?: boolean;
  isUnarchiveEnabled?: boolean;
}

function DeleteButton({
  isCompact,
  remove,
  disableDeleteButtonWithTooltip,
}: FieldActionButtonsProps): JSX.Element | null {
  if (!remove) {
    return null;
  }

  const isDisabled = disableDeleteButtonWithTooltip != null;

  if (isCompact) {
    const button = (
      <Button
        buttonStyle={ButtonStyle.NEGATIVE}
        size={ButtonSize.SMALL}
        text="Delete"
        onClick={remove}
        disabled={isDisabled}
        dataTestAttrs={{component: 'FieldActionButtons', element: 'delete'}}
      />
    );
    return isDisabled ? (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title={disableDeleteButtonWithTooltip}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  }

  if (isDisabled) {
    return (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title={disableDeleteButtonWithTooltip}>
        <span
          role="button"
          aria-label="Delete"
          aria-disabled="true"
          className={classNames(css(styles.singleButton), 'metadata-action faded')}
          {...getTestProps({component: 'FieldActionButtons', element: 'delete'})}
        >
          <BenchlingIcon icon="delete" />
        </span>
      </Tooltip>
    );
  }

  return (
    <a
      aria-label="Delete"
      className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer')}
      {...getActionButtonRoleProps({
        onPress: (e) => {
          remove?.(e as ElementMouseEvent);
        },
      })}
      {...getTestProps({component: 'FieldActionButtons', element: 'delete'})}
    >
      <BenchlingIcon icon="delete" />
    </a>
  );
}

function CancelButton({isCompact, cancel, isCancelEnabled}: FieldActionButtonsProps): JSX.Element | null {
  if (!cancel) {
    return null;
  }

  const isEnabled = isCancelEnabled ?? true;

  if (isCompact) {
    return (
      <Button
        buttonStyle={ButtonStyle.LINK}
        text="Cancel"
        onClick={cancel}
        disabled={!isEnabled}
        dataTestAttrs={{component: 'FieldActionButtons', element: 'cancel'}}
      />
    );
  }

  return (
    <a
      aria-label="Cancel"
      aria-disabled={!isEnabled}
      className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer', {
        inactive: !isEnabled,
      })}
      {...getActionButtonRoleProps({onPress: cancel})}
      {...getTestProps({component: 'FieldActionButtons', element: 'cancel'})}
    >
      <BenchlingIcon className="metadata-action-icon--close" icon="close" />
    </a>
  );
}

function SaveButton({isCompact, complete, isCompleteEnabled}: FieldActionButtonsProps): JSX.Element | null {
  if (!complete) {
    return null;
  }

  if (isCompact) {
    return (
      <Button
        buttonStyle={ButtonStyle.PRIMARY}
        size={ButtonSize.SMALL}
        text="Save"
        onClick={complete}
        disabled={!isCompleteEnabled}
        dataTestAttrs={{component: 'FieldActionButtons', element: 'complete'}}
      />
    );
  }

  return (
    <a
      aria-label="Save"
      aria-disabled={!isCompleteEnabled}
      className={classNames(css(styles.singleButton), 'metadata-action u-primary-pointer', {
        inactive: !isCompleteEnabled,
      })}
      {...getActionButtonRoleProps({onPress: complete})}
      {...getTestProps({component: 'FieldActionButtons', element: 'complete'})}
    >
      <BenchlingIcon
        className={classNames('metadata-action-icon--complete', {
          inactive: !isCompleteEnabled,
        })}
        icon="check"
      />
    </a>
  );
}

function CopyAPIIDButton({copyAPIID}: FieldActionButtonsProps): JSX.Element | null {
  const showAPIID = usePreference('ENABLE_COPY_API_ID')[0];

  if (!copyAPIID || !showAPIID) {
    return null;
  }

  return (
    <Tooltip title="Copy API ID" container="body" placement={TooltipPlacement.TOP}>
      <span
        aria-label="Copy API ID"
        className={classNames(css(styles.singleButton), 'metadata-action faded')}
        {...getActionButtonRoleProps({onPress: copyAPIID})}
        {...getTestProps({component: 'FieldActionButtons', element: 'copy-api-id'})}
      >
        <BenchlingIcon icon="note-code" />
      </span>
    </Tooltip>
  );
}

function ArchiveButton({
  archive,
  isArchiveEnabled,
  disableArchiveButtonWithTooltip,
}: FieldActionButtonsProps): JSX.Element | null {
  if (!archive) {
    return null;
  }

  if (disableArchiveButtonWithTooltip != null) {
    return (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title={disableArchiveButtonWithTooltip}>
        <span
          role="button"
          aria-label="Archive"
          aria-disabled="true"
          className={classNames(css(styles.singleButton), 'metadata-action faded')}
          {...getTestProps({component: 'FieldActionButtons', element: 'archive'})}
        >
          <BenchlingIcon icon="archive" />
        </span>
      </Tooltip>
    );
  }

  const isDisabled = !(isArchiveEnabled ?? true);

  return (
    <Tooltip container="body" placement={TooltipPlacement.TOP} title="Archive">
      <a
        aria-label="Archive"
        aria-disabled={isDisabled}
        className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer', {
          inactive: isDisabled,
        })}
        {...getActionButtonRoleProps({onPress: archive})}
        {...getTestProps({component: 'FieldActionButtons', element: 'archive'})}
      >
        <BenchlingIcon icon="archive" />
      </a>
    </Tooltip>
  );
}

function UnarchiveButton({unarchive, isUnarchiveEnabled}: FieldActionButtonsProps): JSX.Element | null {
  if (!unarchive) {
    return null;
  }

  const isDisabled = !(isUnarchiveEnabled ?? true);

  return (
    <Tooltip container="body" placement={TooltipPlacement.TOP} title="Unarchive">
      <a
        aria-label="Unarchive"
        aria-disabled={isDisabled}
        className={classNames(css(styles.singleButton), 'metadata-action faded u-primary-pointer', {
          inactive: isDisabled,
        })}
        {...getActionButtonRoleProps({onPress: unarchive})}
        {...getTestProps({component: 'FieldActionButtons', element: 'unarchive'})}
      >
        <BenchlingIcon icon="unarchive" />
      </a>
    </Tooltip>
  );
}

function EditButton({edit}: FieldActionButtonsProps): JSX.Element | null {
  if (!edit) {
    return null;
  }

  return (
    <a
      aria-label="Edit"
      className={classNames(css(styles.singleButton), 'metadata-action faded u-primary-pointer')}
      {...getActionButtonRoleProps({onPress: edit})}
      {...getTestProps({component: 'FieldActionButtons', element: 'edit'})}
    >
      <BenchlingIcon icon="edit" />
    </a>
  );
}

function ViewModeButtons(props: FieldActionButtonsProps): JSX.Element {
  return (
    <>
      <CopyAPIIDButton {...props} />
      <DeleteButton {...props} />
      <ArchiveButton {...props} />
      <UnarchiveButton {...props} />
      <EditButton {...props} />
      {props.extraActionButtons?.viewMode}
    </>
  );
}

function EditModeButtons(props: FieldActionButtonsProps): JSX.Element {
  return (
    <>
      <CancelButton {...props} />
      {props.extraActionButtons?.editMode}
      <SaveButton {...props} />
    </>
  );
}

function FieldActionButtons(props: FieldActionButtonsProps): JSX.Element {
  const buttons = props.isEditing ? <EditModeButtons {...props} /> : <ViewModeButtons {...props} />;

  if (props.isCompact) {
    return (
      <span
        className={css(styles.compactTextButtonsWrapper)}
        {...getTestProps({component: 'FieldActionButtons', element: 'compact'})}
      >
        {buttons}
      </span>
    );
  }

  return (
    <div
      className={classNames(props.hasOuterMargin && css(styles.outerMargin), props.className)}
      {...getTestProps({component: 'FieldActionButtons', element: 'root'})}
    >
      <span className={css(styles.buttonsWrapper)}>{buttons}</span>
    </div>
  );
}

FieldActionButtons.defaultProps = {
  className: 'registry-actionGroup',
};

type Args = [string, string, string, string];

FieldActionButtons.propTypes = {
  archive: PropTypes.func,
  cancel: (props: FieldActionButtonsProps, ...args: Args) => {
    if (props.isEditing) {
      return PropTypes.func.isRequired(props, ...args);
    }
    return PropTypes.func(props, ...args);
  },
  className: PropTypes.string.isRequired,
  complete: (props: FieldActionButtonsProps, ...args: Args) => {
    if (props.isEditing) {
      return PropTypes.func.isRequired(props, ...args);
    }
    return PropTypes.func(props, ...args);
  },
  edit: PropTypes.func,
  isCompact: PropTypes.bool,
  isCompleteEnabled: (props: FieldActionButtonsProps, ...args: Args) => {
    if (props.isEditing) {
      return PropTypes.bool.isRequired(props, ...args);
    }
    return PropTypes.bool(props, ...args);
  },
  isEditing: PropTypes.bool.isRequired,
  remove: PropTypes.func,
  unarchive: PropTypes.func,
};

export default FieldActionButtons;

export const styles = StyleSheet.create({
  singleButton: {
    marginLeft: '6px',
    marginRight: '6px',
    padding: '0px',
    display: 'flex',
    alignItems: 'center',
  },
  outerMargin: {marginRight: '8px'},
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  compactTextButtonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
  },
});
